import { isServer } from '@bob/library'
import URLSearchParams from '@ungap/url-search-params'
import {
  SignoutResponse,
  User,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client'

// Creating the userManager triggers an error when loaded in an iframe
// such as in the preview context
// and when running server side render
const isAuthManager = !isServer && process.env.BOB_BUILD_TYPE !== 'preview'

const callbackBaseUrl = `${process.env.SITE_URL}/auth/`
const authority = process.env.IDENTITY_AUTHORITY_URL

const settings: UserManagerSettings = {
  authority,
  client_id: process.env.IDENTITY_CLIENT_ID,
  redirect_uri: `${callbackBaseUrl}callback`,
  post_logout_redirect_uri: `${callbackBaseUrl}logout/callback`,
  automaticSilentRenew: true,
  includeIdTokenInSilentRenew: true,
  response_type: 'code',
  scope: 'openid profile email',
  userStore: isAuthManager ? new WebStorageStateStore({ store: localStorage }) : undefined,
}

let userManager: InstanceType<typeof UserManager> | undefined

if (isAuthManager) {
  userManager = new UserManager(settings)
}

export const getUser = async () => userManager?.getUser() ?? null

const initFlow = (flow: string, locale: string) =>
  userManager?.signinRedirect({
    acr_values: flow,
    state: window.location.href,
    ui_locales: locale,
    extraQueryParams: {
      appContext: false,
      adultexperience: true,
      hideExternalLogin: true,
      hideheader: true,
    },
  })

export const signIn = (locale: string) => initFlow('flow:login', locale)

export const register = (locale: string) => initFlow('flow:register', locale)

// edit profile is the only action that does not have flow
export const edit = (locale: string) => {
  const url = `${authority}/${locale}/profile`
  const returnUrl = encodeURIComponent(`${callbackBaseUrl}callback`)

  window.location.href = `${url}?clientid=${settings.client_id}&returnUrl=${returnUrl}&adultexperience=true&hideexternallogin=true&state=${window.location.href}`
}

export const signOut = () =>
  userManager?.signoutRedirect({
    state: window.location.href,
  })

const getStateIfOnlyParam = (): string | null => {
  const searchParams = new URLSearchParams(window.location.search)
  const state = searchParams.get('state')
  let numParams = 0
  searchParams.forEach(() => numParams++)

  return numParams === 1 ? state : null
}

export const signInCallback = (callback?: (u: User) => Promise<unknown>) => {
  const state = getStateIfOnlyParam()

  if (state) {
    // the state query parameter is only present
    // when the user returns from the edit profile
    window.location.replace(state)

    return Promise.resolve()
  }

  return userManager
    ?.signinRedirectCallback()
    .then((res) => callback?.(res))
    .catch(() => window.location.replace('/'))
}

export const signOutCallback = (callback?: (r: SignoutResponse) => Promise<unknown>) =>
  userManager
    ?.signoutRedirectCallback()
    .then((res) => callback?.(res))
    .catch(() => window.location.replace('/'))
